import * as PIXI from 'pixi.js';
import { Config } from '../../video-data/config';

import { assertNumber } from '../../validators';
import { AlphaAttribute, ColorAttribute } from '../attributes';
import { DynamicStylingMixin } from '../dynamicStylingMixin';
import type { BaseAssetSettings } from './BaseAsset';
import { BaseAsset } from './BaseAsset';

/**
 * A CircleAsset Object will load the specified solid color.
 */
export class CircleAsset extends DynamicStylingMixin(BaseAsset) {
    override _classType = Config.CIRCLE_ASSET_TYPE;

    @ColorAttribute()
    accessor color = 0xeeeb77;
    #lineThickness = 0;
    @ColorAttribute()
    accessor lineColor = 0xeeeb77;
    @AlphaAttribute()
    accessor fillAlpha = 1;

    #radius = 10;
    #radiusOffset = 0;

    #graphics: PIXI.Graphics = undefined;

    // TODO(strict-ts): Change container type when BaseAsset.ts is updated
    constructor(container: unknown, settings: BaseAssetSettings) {
        super(container, settings);

        this.#graphics = new PIXI.Graphics();
        this._addPixiChild(this.#graphics);
    }

    /**
     * This asset doesn't have to load anything remotely so it should "always load"
     */
    override get _shouldLoad() {
        return true;
    }

    override isMorphMatch(other: BaseAsset): boolean {
        const otherCircle = other as CircleAsset;
        if (!Config.isCircleAsset(otherCircle)) {
            return false;
        }

        if (this.color !== otherCircle.color) {
            return false;
        }
        if (this.fillAlpha !== otherCircle.fillAlpha) {
            return false;
        }

        return true;
    }

    get radius(): number {
        return this.#radius;
    }

    set radius(value) {
        if (value < 0) {
            value = 0;
        }
        if (this.radius !== value) {
            this.#radius = value;
            this.requiresRedraw = true;
        }
    }

    /**
     * Get the line thickness
     */
    get lineThickness(): number {
        return this.#lineThickness;
    }

    set lineThickness(value: number) {
        assertNumber(value);
        if (this.lineThickness !== value) {
            this.#lineThickness = value;
            this.requiresRedraw = true;
        }
    }

    get radiusOffset(): number {
        return this.#radiusOffset;
    }

    set radiusOffset(value: number) {
        assertNumber(value);
        if (this.radiusOffset !== value) {
            this.#radiusOffset = value;
            this.requiresRedraw = true;
        }
    }

    override _build() {
        this.clearAnimation();

        super._build();

        if (this.hasDynamicStyle || this.hasMorphAnimation) {
            this._prepareDynamicStylingForUpdate();
            this.applyTransformation();
            this._applyAnimation();
            this._updateDynamicStyling();
        } else {
            this.applyTransformation();
            this._applyAnimation();
        }

        this._applyIntroAnimations();
        this._applyOutroAnimations();
        this.animationManager.setInitValues();
    }

    override _draw() {
        const totalRadius = this.radius + this.radiusOffset;
        const lineOffset = this.lineThickness / 2;
        const centerOffset = totalRadius + lineOffset;

        this.#graphics.clear();
        this.#graphics.lineStyle(this.lineThickness, this.lineColor, 1);
        this.#graphics.beginFill(this.color, this.fillAlpha);
        this.#graphics.drawCircle(centerOffset, centerOffset, totalRadius);
        this.#graphics.endFill();
    }

    override get propertiesToSerialize() {
        return super.propertiesToSerialize.concat([
            'mask',
            'lineThickness',
            'lineColor',
            'color',
            'fillAlpha',
            'radius',
            'radiusOffset',
        ]);
    }
}
